import { useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO, differenceInCalendarDays, isBefore } from "date-fns";
import { AppHeader } from "@/components/app/AppHeader";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import {
  fetchCalendarEntries,
  COLOR_TAG_HEX,
  DEPT_KEY_LABEL,
  type CalendarEntryWithRelations,
} from "@/lib/calendar";

export const Route = createFileRoute("/_app/calendar_/intelligence")({
  component: IntelligencePage,
});

function IntelligencePage() {
  const { profile, role } = useAuth();
  const isAdmin = role === "admin" || role === "moderator";
  const dept = profile?.department;

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["calendar-entries"],
    queryFn: fetchCalendarEntries,
  });

  const scoped: CalendarEntryWithRelations[] = useMemo(() => {
    if (isAdmin) return entries;
    if (!dept) return entries.filter((e) => e.visibility_scope === "global");
    return entries.filter(
      (e) =>
        e.visibility_scope === "global" ||
        e.departments.includes(dept) ||
        e.assignees.some((a) => a.user_id === profile?.id),
    );
  }, [entries, isAdmin, dept, profile?.id]);

  const today = new Date();
  const upcoming = scoped
    .filter((e) => !isBefore(parseISO(e.start_date), today))
    .sort((a, b) => a.start_date.localeCompare(b.start_date));

  const within7 = upcoming.filter(
    (e) => differenceInCalendarDays(parseISO(e.start_date), today) <= 7,
  );
  const within30 = upcoming.filter(
    (e) => differenceInCalendarDays(parseISO(e.start_date), today) <= 30,
  );

  // Conflicts: same date, multiple entries hitting the same department
  const conflicts = useMemo(() => {
    const byDateDept = new Map<string, CalendarEntryWithRelations[]>();
    for (const e of scoped) {
      const depts = e.departments.length ? e.departments : ["all"];
      for (const d of depts) {
        const k = `${e.start_date}::${d}`;
        const arr = byDateDept.get(k) ?? [];
        arr.push(e);
        byDateDept.set(k, arr);
      }
    }
    return Array.from(byDateDept.entries())
      .filter(([, arr]) => arr.length > 1)
      .map(([k, arr]) => {
        const [date, d] = k.split("::");
        return { date, dept: d, entries: arr };
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [scoped]);

  const projectLinked = upcoming.filter((e) => e.linked_project);

  const byType = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of scoped) m.set(e.entry_type, (m.get(e.entry_type) ?? 0) + 1);
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [scoped]);

  return (
    <div className="flex flex-col gap-6 px-6 py-6">
      <AppHeader
        subtitle={
          isAdmin
            ? "Calendar Intelligence · All-department planning insights"
            : `Calendar Intelligence · ${dept ? DEPT_KEY_LABEL[dept] : "your team"}`
        }
        extraActions={
          <Button asChild variant="outline" size="sm" className="h-9 text-[11px]">
            <Link to="/calendar">Back to Calendar</Link>
          </Button>
        }
      />

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Upcoming (Next 7 days)" value={within7.length} />
            <Kpi label="Upcoming (Next 30 days)" value={within30.length} />
            <Kpi label="Conflicts Detected" value={conflicts.length} tone={conflicts.length ? "danger" : "default"} />
            <Kpi label="Project-linked Deadlines" value={projectLinked.length} />
          </div>

          <Section title="Conflicts (Same date · Same department)">
            {conflicts.length === 0 ? (
              <Empty>No conflicts found.</Empty>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                    <tr>
                      <Th>Date</Th>
                      <Th>Department</Th>
                      <Th>Conflicting Entries</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {conflicts.map((c, i) => (
                      <tr key={i} className="border-t border-border">
                        <Td>{format(parseISO(c.date), "EEE, dd MMM")}</Td>
                        <Td>
                          {c.dept === "all"
                            ? "All / Global"
                            : DEPT_KEY_LABEL[c.dept as keyof typeof DEPT_KEY_LABEL] ?? c.dept}
                        </Td>
                        <Td>
                          <div className="flex flex-wrap gap-2">
                            {c.entries.map((e) => (
                              <Pill key={e.id} entry={e} />
                            ))}
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section title="Project-linked Deadlines">
            {projectLinked.length === 0 ? (
              <Empty>No project-linked deadlines.</Empty>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                    <tr>
                      <Th>Date</Th>
                      <Th>Title</Th>
                      <Th>Project</Th>
                      <Th>Departments</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectLinked.map((e) => (
                      <tr key={e.id} className="border-t border-border">
                        <Td>{format(parseISO(e.start_date), "EEE, dd MMM")}</Td>
                        <Td>
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block h-2 w-2 rounded-full"
                              style={{ background: COLOR_TAG_HEX[e.color_tag] }}
                            />
                            {e.title}
                          </div>
                        </Td>
                        <Td>{e.linked_project}</Td>
                        <Td>
                          {e.departments.length
                            ? e.departments
                                .map((d) => DEPT_KEY_LABEL[d] ?? d)
                                .join(", ")
                            : "Global"}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section title="Entry Volume by Type">
            {byType.length === 0 ? (
              <Empty>No entries yet.</Empty>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                {byType.map(([t, n]) => (
                  <div
                    key={t}
                    className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-sm"
                  >
                    <span className="capitalize text-muted-foreground">
                      {t.replace("_", " ")}
                    </span>
                    <span className="font-semibold">{n}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "danger";
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={
          "mt-2 text-3xl font-semibold " +
          (tone === "danger" && value > 0 ? "text-destructive" : "text-foreground")
        }
      >
        {value}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 text-left font-medium">{children}</th>;
}
function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-3 py-2 align-top">{children}</td>;
}
function Pill({ entry }: { entry: CalendarEntryWithRelations }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2 py-0.5 text-xs"
      title={entry.entry_type}
    >
      <span
        className="inline-block h-1.5 w-1.5 rounded-full"
        style={{ background: COLOR_TAG_HEX[entry.color_tag] }}
      />
      {entry.title}
    </span>
  );
}
